import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  User,
  ApiKey,
  ApiKeyCreateResponse,
  ApiKeyScope,
  Webhook,
  WebhookCreateResponse,
  WebhookEvent,
  MembersResponse,
  InviteResponse,
  TeamRole,
  SupportTicket,
  SupportTicketDetail,
  SupportTicketCreateResponse,
  Issuer,
  IssuerSettings,
  Badge,
  BadgeTemplate,
  Draft,
  DraftListItem,
  AwardListResponse,
  BulkSendJob,
  BulkSendItem,
  BulkSendItemOutcome,
  UsageResponse,
  EngagementResponse,
  AwardDelivery,
  EmailStats,
  AnalyticsResponse,
  ActivityResponse,
  InvitePreview
} from '../types';
import { isMockAuth, getMockAuthSession } from '../test-utils/mock-auth';

const API_URL = import.meta.env.VITE_API_URL as string;

async function getAuthToken(): Promise<string> {
  if (isMockAuth()) {
    const session = getMockAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('Not authenticated');
    return token;
  }
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  let token: string;
  try {
    token = await getAuthToken();
  } catch {
    window.location.href = '/signin';
    throw new Error('Session expired');
  }
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }
  });
  if (response.status === 401) {
    window.location.href = '/signin';
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    // Lambda errors come as { error: ... } via errorToApiGateway.
    // AWS API Gateway native errors come as { message: ... }.
    // Fall back to statusText + status code so users always see something useful.
    const errBody = body as { error?: string; message?: string } | null;
    const msg = errBody?.error || errBody?.message || response.statusText || 'Unknown error';
    // Attach the HTTP status so the global fault reporter can decide whether to
    // auto-raise a support ticket (form-gap 400s + server 5xx, not auth/expected).
    const err = new Error(`${msg} (HTTP ${response.status})`) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return response.json() as Promise<T>;
}

// Unauthenticated request for PUBLIC endpoints (e.g. the team-invite preview and
// set-password accept). No Authorization header, and — unlike apiRequest — a 401
// never redirects to /signin, since the caller here is intentionally logged out.
async function publicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errBody = body as { error?: string; message?: string } | null;
    const msg = errBody?.error || errBody?.message || response.statusText || 'Unknown error';
    const err = new Error(`${msg} (HTTP ${response.status})`) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return response.json() as Promise<T>;
}

export const api = {
  // User
  getUser: () => apiRequest<User>('/user/profile'),

  // Plan usage / limits — drives the limit-reached banners.
  getUsage: () => apiRequest<UsageResponse>('/usage'),

  // Billing
  createCheckout: (plan: string) =>
    apiRequest<{ url: string }>('/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan })
    }),

  createPortal: () =>
    apiRequest<{ url: string }>('/stripe/portal', { method: 'POST' }),

  // D1 — self-serve "send yourself a test badge" (seeds a sample issuer+badge+award).
  sendTestBadge: () =>
    apiRequest<{ ok: boolean; reason?: string; awardViewUrl?: string }>('/user/send-test-badge', { method: 'POST' }),

  // D5 — recipient engagement rollup across the user's awards.
  getEngagement: () => apiRequest<EngagementResponse>('/engagement'),

  // Per-award email delivery status (EMAIL_LOG). status: send|delivery|open|bounce|complaint.
  getAwardDelivery: (awardGuid: string) =>
    apiRequest<AwardDelivery>(`/awards/${encodeURIComponent(awardGuid)}/delivery`),

  // Aggregate email engagement for the signed-in issuer (Analytics page).
  getEmailStats: (range = '30d') =>
    apiRequest<EmailStats>(`/me/email-stats?range=${encodeURIComponent(range)}`),

  getAnalytics: (range = '30d') =>
    apiRequest<AnalyticsResponse>(`/me/analytics?range=${encodeURIComponent(range)}`),

  getActivity: (before?: number | null, limit = 40) =>
    apiRequest<ActivityResponse>(`/me/activity?limit=${limit}${before ? `&before=${before}` : ''}`),

  // D6 — conversion micro-survey ("what's stopping you from issuing?").
  submitSurvey: (reason: string, text?: string) =>
    apiRequest<{ ok: boolean }>('/user/conversion-survey', {
      method: 'POST',
      body: JSON.stringify({ reason, text })
    }),

  // API keys
  listApiKeys: () => apiRequest<ApiKey[]>('/apikeys'),

  createApiKey: (name: string, scopes?: ApiKeyScope[]) =>
    apiRequest<ApiKeyCreateResponse>('/apikeys', {
      method: 'POST',
      body: JSON.stringify({ name, ...(scopes ? { scopes } : {}) })
    }),

  deleteApiKey: (key: string) =>
    apiRequest<{ success: boolean }>(`/apikeys/${encodeURIComponent(key)}`, {
      method: 'DELETE'
    }),

  // Webhooks
  listWebhooks: () =>
    apiRequest<{ webhooks: Webhook[] }>('/webhooks').then((r) => r.webhooks),

  createWebhook: (url: string, events: WebhookEvent[]) =>
    apiRequest<WebhookCreateResponse>('/webhooks', {
      method: 'POST',
      body: JSON.stringify({ url, events })
    }),

  deleteWebhook: (id: string) =>
    apiRequest<{ success: boolean }>(`/webhooks/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    }),

  // Team members
  listMembers: () => apiRequest<MembersResponse>('/members'),

  inviteMember: (email: string, role: TeamRole) =>
    apiRequest<InviteResponse>('/members', {
      method: 'POST',
      body: JSON.stringify({ email, role })
    }),

  acceptInvite: (inviteId: string) =>
    apiRequest<{ accountId: string; role: TeamRole; status: string }>('/members/accept', {
      method: 'POST',
      body: JSON.stringify({ inviteId })
    }),

  // Public invite preview — no auth (the invitee is logged out).
  previewInvite: (inviteId: string) =>
    publicRequest<InvitePreview>(`/members/${encodeURIComponent(inviteId)}`),

  // Public set-password accept — sets the pre-created invitee's password and joins
  // them to the team. Afterwards the invitee can sign in with email + password.
  acceptInviteWithPassword: (inviteId: string, password: string) =>
    publicRequest<{ accountId: string; role: TeamRole; email: string }>('/members/accept-password', {
      method: 'POST',
      body: JSON.stringify({ inviteId, password })
    }),

  updateMemberRole: (memberId: string, role: TeamRole) =>
    apiRequest<{ memberId: string; role: TeamRole }>(`/members/${encodeURIComponent(memberId)}/role`, {
      method: 'POST',
      body: JSON.stringify({ role })
    }),

  removeMember: (memberId: string) =>
    apiRequest<string>(`/members/${encodeURIComponent(memberId)}`, {
      method: 'DELETE'
    }),

  // Support tickets
  createSupportTicket: (subject: string, message: string, category: string) =>
    apiRequest<SupportTicketCreateResponse>('/support', {
      method: 'POST',
      body: JSON.stringify({ subject, message, category })
    }),

  listSupportTickets: () => apiRequest<SupportTicket[]>('/support'),

  getSupportTicket: (ticketId: string) =>
    apiRequest<SupportTicketDetail>(`/support/${encodeURIComponent(ticketId)}`),

  replySupportTicket: (ticketId: string, message: string) =>
    apiRequest<{ success: boolean }>(`/support/${encodeURIComponent(ticketId)}/reply`, {
      method: 'POST',
      body: JSON.stringify({ message })
    }),

  // Issuers
  listIssuers: () => apiRequest<Issuer[]>('/issuers'),

  createIssuer: (data: { name: string; url: string; email: string; logo?: string; linkedinOrganizationId?: string }) =>
    apiRequest<{ issuerId: string }>('/issuers', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  editIssuer: (issuerId: string, data: { name: string; url: string; email: string; logo?: string; linkedinOrganizationId?: string }) =>
    apiRequest<{ issuerId: string; updated: boolean }>(`/issuers/${encodeURIComponent(issuerId)}`, {
      method: 'PUT',
      body: JSON.stringify({ issuerId, ...data })
    }),

  verifyIssuer: (issuerId: string, code: string) =>
    apiRequest<string>(`/issuers/${encodeURIComponent(issuerId)}/verify`, {
      method: 'POST',
      body: JSON.stringify({ issuerId, code })
    }),

  deleteIssuer: (issuerId: string) =>
    apiRequest<string>(`/issuers/${encodeURIComponent(issuerId)}`, {
      method: 'DELETE',
      body: JSON.stringify({ issuerId })
    }),

  getIssuerSettings: (issuerId: string) =>
    apiRequest<{ settings: IssuerSettings }>(`/issuers/${encodeURIComponent(issuerId)}/settings`).then((r) => r.settings),

  updateIssuerSettings: (issuerId: string, patch: IssuerSettings) =>
    apiRequest<{ settings: IssuerSettings; updated: boolean }>(`/issuers/${encodeURIComponent(issuerId)}/settings`, {
      method: 'POST',
      body: JSON.stringify(patch)
    }).then((r) => r.settings),

  // Badges
  listBadges: () => apiRequest<Badge[]>('/badges'),

  // Returns the full badge document including the editable Fabric design JSON.
  // The list endpoint deliberately omits designJson to keep responses lean.
  getBadge: (badgeId: string) =>
    apiRequest<Badge>(`/badges/${encodeURIComponent(badgeId)}`),

  /** Render the current design SVG with SAMPLE merge data (same path as issuance). */
  previewBadgeImage: (designSvg: string) =>
    apiRequest<{ image: string }>('/badges/preview-image', {
      method: 'POST',
      body: JSON.stringify({ designSvg }),
    }),

  createBadge: (data: { name: string; description: string; criteria: string; image: string; issuerId: string; shareText?: string; designJson?: string; designSvg?: string }) =>
    apiRequest<{ badgeId: string }>('/badges', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  updateBadge: (badgeId: string, data: { name?: string; description?: string; criteria?: string; image?: string; designJson?: string; designSvg?: string }) =>
    apiRequest<{ badgeId: string; updated: boolean }>(`/badges/${encodeURIComponent(badgeId)}`, {
      method: 'PUT',
      body: JSON.stringify({ badgeId, ...data })
    }),

  updateBadgeShareText: (badgeId: string, text: string) =>
    apiRequest<{ badgeId: string }>(`/badges/${encodeURIComponent(badgeId)}/share-text`, {
      method: 'PUT',
      body: JSON.stringify({ badgeId, text })
    }),

  getBadgeCertificate: (badgeId: string) =>
    apiRequest<{ certificateSvg: string | null }>(`/badges/${encodeURIComponent(badgeId)}/certificate`).then((r) => r.certificateSvg),

  saveBadgeCertificate: (badgeId: string, certificateSvg: string) =>
    apiRequest<{ badgeId: string; updated: boolean; hasCertificate: boolean }>(`/badges/${encodeURIComponent(badgeId)}/certificate`, {
      method: 'POST',
      body: JSON.stringify({ certificateSvg })
    }),

  deleteBadge: (badgeId: string) =>
    apiRequest<string>(`/badges/${encodeURIComponent(badgeId)}`, {
      method: 'DELETE',
      body: JSON.stringify({ badgeId })
    }),

  // Account badge templates — a saved design a user can reuse.
  listTemplates: () => apiRequest<{ items: BadgeTemplate[] }>('/templates'),
  getTemplate: (id: string) => apiRequest<BadgeTemplate>(`/templates/${encodeURIComponent(id)}`),
  createTemplate: (data: { name: string; designJson: string; image?: string }) =>
    apiRequest<{ templateId: string }>('/templates', { method: 'POST', body: JSON.stringify(data) }),
  deleteTemplate: (id: string) =>
    apiRequest<{ templateId: string }>(`/templates/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ templateId: id }) }),

  // Badge drafts — server-side, cross-device badges-in-progress.
  listDrafts: () => apiRequest<{ items: DraftListItem[] }>('/drafts'),
  getDraft: (id: string) => apiRequest<Draft>(`/drafts/${encodeURIComponent(id)}`),
  createDraft: (data: Partial<Draft>) =>
    apiRequest<{ draftId: string }>('/drafts', { method: 'POST', body: JSON.stringify(data) }),
  updateDraft: (id: string, data: Partial<Draft>) =>
    apiRequest<{ draftId: string; updated: boolean }>(`/drafts/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ draftId: id, ...data }) }),
  deleteDraft: (id: string) =>
    apiRequest<{ draftId: string }>(`/drafts/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ draftId: id }) }),

  // Awards
  listAwards: (
    filter?: { badgeId?: string; search?: string; searchField?: 'name' | 'email'; notEmailedOnly?: boolean; count?: boolean },
    lastEvaluatedKey?: string
  ) => {
    const params = new URLSearchParams();
    if (filter && (filter.badgeId || filter.search || filter.searchField || filter.notEmailedOnly || filter.count)) {
      params.set('filter', JSON.stringify(filter));
    }
    if (lastEvaluatedKey) params.set('lastEvaluatedKey', lastEvaluatedKey);
    const qs = params.toString();
    return apiRequest<AwardListResponse>(`/awards${qs ? `?${qs}` : ''}`);
  },

  createAward: (data: { badgeId: string; recipient: { name: string; email: string }; issuedOn: number; expires?: number }) =>
    apiRequest<{ awardId: string }>('/awards', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  sendAward: (awardId: string, email: string) =>
    apiRequest<string>(`/awards/${encodeURIComponent(awardId)}/send`, {
      method: 'POST',
      body: JSON.stringify({ awardId, email })
    }),

  // Durable server-side bulk send. Two selection modes:
  //  • { awardIds } — an explicit list (checkbox selections).
  //  • { filter }   — a scope the SERVER resolves to the full matching set
  //    ("select all N" / "send all not-emailed"), so the browser never has to
  //    page thousands of ids and can't silently cover only the visible page.
  // The worker filters non-sendable (revoked / no email) and reports them as
  // skipped. Every send becomes a durable, resumable job (poll / re-open by id).
  startBulkSend: (scope: {
    /** What to do to each selected credential (default 'send'). */
    action?: 'send' | 'revoke' | 'delete';
    awardIds?: string[];
    filter?: { badgeId?: string; search?: string; searchField?: 'name' | 'email'; notEmailedOnly?: boolean };
    /** send: re-send to recipients already emailed (default: skip them). */
    resend?: boolean;
    /** revoke: optional reason + whether to email recipients. */
    reason?: string;
    notify?: boolean;
  }) =>
    apiRequest<{ jobId: string; total: number }>(`/awards/bulk-send`, {
      method: 'POST',
      body: JSON.stringify(scope)
    }),
  getBulkSendJob: (jobId: string) =>
    apiRequest<BulkSendJob>(`/awards/bulk-send/${encodeURIComponent(jobId)}`),
  // Jobs history for the account (newest first).
  listBulkSendJobs: (cursor?: string) => {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return apiRequest<{ jobs: BulkSendJob[]; nextCursor: string | null }>(`/awards/bulk-send${qs}`);
  },
  // A page of the per-recipient ledger (intention→execution) for one job.
  getBulkSendItems: (jobId: string, opts?: { cursor?: string; outcome?: BulkSendItemOutcome }) => {
    const params = new URLSearchParams({ items: '1' });
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.outcome) params.set('outcome', opts.outcome);
    return apiRequest<BulkSendJob & { items: BulkSendItem[]; nextCursor: string | null }>(
      `/awards/bulk-send/${encodeURIComponent(jobId)}?${params.toString()}`
    );
  },

  shareAward: (awardId: string, recipients: string, subject: string, message: string) =>
    apiRequest<string>(`/awards/${encodeURIComponent(awardId)}/share`, {
      method: 'POST',
      body: JSON.stringify({ awardId, recipients, subject, message })
    }),

  // Revoke an award (Open Badge v2.0: marks the assertion revoked). Kept for audit.
  revokeAward: (awardId: string, reason?: string, notify?: boolean) =>
    apiRequest<{ revoked: boolean; awardId: string }>(`/awards/${encodeURIComponent(awardId)}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ awardId, reason, notify })
    }),

  // Edit-before-send — correct an UNSENT, non-revoked credential in place. Only the
  // provided fields change; the award keeps its id/URL. Rejected once emailed.
  editAward: (
    awardId: string,
    fields: { name?: string; email?: string; issuedOn?: string; expires?: string | null }
  ) =>
    apiRequest<{ updated: boolean; awardId: string; award: unknown }>(`/awards/${encodeURIComponent(awardId)}/edit`, {
      method: 'POST',
      body: JSON.stringify({ awardId, ...fields })
    }),

  // Hard-delete an award (for mistakes). Removes the record entirely.
  deleteAward: (awardId: string) =>
    apiRequest<{ deleted: boolean; awardId: string }>(`/awards/${encodeURIComponent(awardId)}`, {
      method: 'DELETE',
      body: JSON.stringify({ awardId })
    })
};
